import { createTheme, alpha } from "@mui/material/styles";

const primaryMain = "#662871";
const secondaryMain = "#F3931F";
const INPUT_HEIGHT = 48;

export const MuiTheme = createTheme({
  direction: "rtl",
  palette: {
    mode: "light",
    primary: {
      main: primaryMain,
      light: "#8B4A96",
      dark: "#4A1A52",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: secondaryMain,
      light: "#F5A84D",
      dark: "#C47619",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#2E7D32",
      light: "#4CAF50",
      dark: "#1B5E20",
    },
    warning: {
      main: "#ED6C02",
      light: "#FF9800",
      dark: "#E65100",
    },
    error: {
      main: "#D32F2F",
      light: "#EF5350",
      dark: "#C62828",
    },
    info: {
      main: "#0288D1",
      light: "#03A9F4",
      dark: "#01579B",
    },
    background: {
      default: "#EEF1F6",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A1A2E",
      secondary: "#6B7280",
      disabled: "#9CA3AF",
    },
    divider: "#E5E7EB",
    grey: {
      50: "#F9FAFB",
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
    },
  },
  typography: {
    fontFamily: '"Beiruti", sans-serif',
    h1: {
      fontSize: "1.75rem",
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 700,
      lineHeight: 1.35,
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "0.875rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "0.9375rem",
      lineHeight: 1.6,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.5,
      color: "#6B7280",
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "none",
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    "none",
    "0 1px 2px rgba(0, 0, 0, 0.05)",
    "0 1px 3px rgba(0, 0, 0, 0.08)",
    "0 2px 4px rgba(0, 0, 0, 0.08)",
    "0 2px 8px rgba(0, 0, 0, 0.1)",
    "0 4px 12px rgba(0, 0, 0, 0.1)",
    "0 4px 16px rgba(0, 0, 0, 0.12)",
    "0 8px 24px rgba(0, 0, 0, 0.12)",
    "0 12px 32px rgba(0, 0, 0, 0.14)",
    "0 16px 40px rgba(0, 0, 0, 0.16)",
    "0 20px 48px rgba(0, 0, 0, 0.18)",
    "0 24px 56px rgba(0, 0, 0, 0.2)",
    "0 28px 64px rgba(0, 0, 0, 0.22)",
    "0 32px 72px rgba(0, 0, 0, 0.24)",
    "0 36px 80px rgba(0, 0, 0, 0.26)",
    "0 40px 88px rgba(0, 0, 0, 0.28)",
    "0 44px 96px rgba(0, 0, 0, 0.3)",
    "0 48px 104px rgba(0, 0, 0, 0.32)",
    "0 52px 112px rgba(0, 0, 0, 0.34)",
    "0 56px 120px rgba(0, 0, 0, 0.36)",
    "0 60px 128px rgba(0, 0, 0, 0.38)",
    "0 64px 136px rgba(0, 0, 0, 0.4)",
    "0 68px 144px rgba(0, 0, 0, 0.42)",
    "0 72px 152px rgba(0, 0, 0, 0.44)",
    "0 76px 160px rgba(0, 0, 0, 0.46)",
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha(theme.palette.grey[400], 0.4)} ${alpha(theme.palette.grey[200], 0.2)}`,
          "&::-webkit-scrollbar": {
            width: 5,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: alpha(theme.palette.grey[200], 0.2),
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.grey[400], 0.4),
            borderRadius: 4,
          },
        },
        a: {
          color: "inherit",
          textDecoration: "none",
        },
        button: {
          outline: "none",
        },
      }),
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "8px 16px",
          fontWeight: 600,
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#4A1A52",
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
          boxShadow: `0 1px 2px ${alpha("#1A1A2E", 0.04)}, 0 6px 20px ${alpha("#1A1A2E", 0.05)}`,
          borderRadius: 14,
          backgroundColor: theme.palette.background.paper,
          transition: "box-shadow 0.28s ease, border-color 0.28s ease, transform 0.28s ease",
          "&:hover": {
            borderColor: alpha(theme.palette.primary.main, 0.18),
            boxShadow: `0 4px 8px ${alpha("#1A1A2E", 0.05)}, 0 12px 28px ${alpha(theme.palette.primary.main, 0.1)}`,
          },
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: "none",
        }),
        outlined: ({ theme }) => ({
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.background.paper,
            minHeight: INPUT_HEIGHT,
            "& fieldset": {
              borderColor: theme.palette.divider,
            },
            "&:hover fieldset": {
              borderColor: theme.palette.grey[400],
            },
            "&.Mui-focused fieldset": {
              borderColor: theme.palette.primary.main,
            },
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          minHeight: INPUT_HEIGHT,
          backgroundColor: theme.palette.background.paper,
          "& fieldset": {
            borderColor: theme.palette.divider,
          },
          "&:hover fieldset": {
            borderColor: theme.palette.grey[400],
          },
          "&.Mui-focused fieldset": {
            borderColor: theme.palette.primary.main,
          },
        }),
        input: {
          padding: "12px 14px",
          boxSizing: "border-box",
          height: "auto",
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            minHeight: INPUT_HEIGHT,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: `${INPUT_HEIGHT}px !important`,
          display: "flex",
          alignItems: "center",
          boxSizing: "border-box",
          padding: "12px 14px",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius * 1.5,
          boxShadow: theme.shadows[6],
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          marginBottom: 2,
          "&.Mui-selected": {
            backgroundColor: alpha(theme.palette.common.white, 0.15),
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.2),
            },
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.875rem",
        },
      },
    },
  },
});

export const CHART_COLORS = [
  primaryMain,
  secondaryMain,
  "#4CAF50",
  "#0288D1",
  "#9C27B0",
  "#FF5722",
  "#607D8B",
  "#795548",
  "#E91E63",
];
